import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateValueType } from 'react-tailwindcss-datepicker/dist/types';
import { toast } from '../../../../ui/toast';
import { getEmployeeList } from '../../../../utils/api_service/employees';
import { getJobOpeningList } from '../../../../utils/api_service/JobOpenings';
import useDebouncedEmployeeSearch from '../../../../utils/helpers/AsyncSelect/EmployeeGet';
import { EmployeeUser } from '../../../../utils/types/admin_types';
import {
  JobOpeningList,
  JobOpeningRequest,
  sortJobOpening,
} from '../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../utils/types/react_select_types';

export const useJobOpenings = () => {
  const history = useHistory();

  const [jobOpeningData, setJobOpeningData] = useState<JobOpeningList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [defaultEmployeeUserList, setDefaultEmployeeUserList] = useState<
    EmployeeUser[]
  >([]);

  // Draft States (for the drawer UI)
  const [draftHiringManager, setDraftHiringManager] =
    useState<SelectOptions | undefined>();
  const [draftInternalRecruiter, setDraftInternalRecruiter] =
    useState<SelectOptions | undefined>();
  const [draftJobReferenceNumber, setDraftJobReferenceNumber] =
    useState<string>('');
  const [draftJobTitle, setDraftJobTitle] = useState<string>('');
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [draftDateRange, setDraftDateRange] = useState<DateValueType>({
    startDate: null,
    endDate: null,
  });

  // Applied States (for the API calls)
  const [appliedHiringManager, setAppliedHiringManager] =
    useState<SelectOptions | undefined>();
  const [appliedInternalRecruiter, setAppliedInternalRecruiter] =
    useState<SelectOptions | undefined>();
  const [appliedJobReferenceNumber, setAppliedJobReferenceNumber] =
    useState<string>('');
  const [appliedJobTitle, setAppliedJobTitle] = useState<string>('');
  const [appliedTags, setAppliedTags] = useState<string[]>([]);
  const [appliedDateRange, setAppliedDateRange] = useState<DateValueType>({
    startDate: null,
    endDate: null,
  });

  const [activeTab, setActiveTab] = useState<string>('Active Job Openings');
  const tabs = [
    { label: 'Active Job Openings' },
    { label: 'Closed Job Openings' },
  ];
  const [sort, setSort] = useState<sortJobOpening>({ creationDate: 'desc' });
  const [isFiltersApplied, setIsFiltersApplied] = useState<boolean>(false);
  const { loadOptionsDebounced } = useDebouncedEmployeeSearch();

  // Compute applied filters to show in the AppliedFilters component
  const appliedFilters: { [label: string]: string[] } = {};

  if (appliedJobTitle) appliedFilters['Job Title'] = [appliedJobTitle];
  if (appliedJobReferenceNumber)
    appliedFilters['Job Reference'] = [appliedJobReferenceNumber];
  if (appliedHiringManager)
    appliedFilters['Hiring Manager'] = [appliedHiringManager.label];
  if (appliedInternalRecruiter)
    appliedFilters['Internal Recruiter'] = [appliedInternalRecruiter.label];
  if (appliedTags.length > 0) appliedFilters['Tags'] = appliedTags;

  const handleOpenFilter = () => {
    // When opening, sync draft with already applied filters
    setDraftJobTitle(appliedJobTitle);
    setDraftJobReferenceNumber(appliedJobReferenceNumber);
    setDraftHiringManager(appliedHiringManager);
    setDraftInternalRecruiter(appliedInternalRecruiter);
    setDraftTags(appliedTags);
    setDraftDateRange(appliedDateRange);
    setFilterOpen(true);
  };

  const handleCloseFilter = () => {
    setFilterOpen(false);
  };
  // Clear all filters and reset state (Reset button)
  const handleClearFilters = () => {
    setAppliedJobTitle('');
    setAppliedJobReferenceNumber('');
    setAppliedHiringManager(undefined);
    setAppliedInternalRecruiter(undefined);
    setAppliedTags([]);
    setAppliedDateRange({ startDate: null, endDate: null });

    // Also clear drafts to stay in sync
    setDraftJobTitle('');
    setDraftJobReferenceNumber('');
    setDraftHiringManager(undefined);
    setDraftInternalRecruiter(undefined);
    setDraftTags([]);
    setDraftDateRange({ startDate: null, endDate: null });

    setIsFiltersApplied(false);
    setPageNumber(1);
    handleCloseFilter();
    // fetch is triggered by useEffect on applied states
  };

  // User clicked "Apply"
  const handleApplyFilters = () => {
    setAppliedJobTitle(draftJobTitle);
    setAppliedJobReferenceNumber(draftJobReferenceNumber);
    setAppliedHiringManager(draftHiringManager);
    setAppliedInternalRecruiter(draftInternalRecruiter);
    setAppliedTags(draftTags);
    setAppliedDateRange(draftDateRange);

    setIsFiltersApplied(true);
    setPageNumber(1);
    handleCloseFilter();
  };

  // Remove a specific filter (from top chip section)
  const handleRemoveFilter = (label: string, value: string | null) => {
    setPageNumber(1);
    switch (label) {
      case 'Job Title':
        setAppliedJobTitle('');
        setDraftJobTitle('');
        break;
      case 'Job Reference':
        setAppliedJobReferenceNumber('');
        setDraftJobReferenceNumber('');
        break;
      case 'Hiring Manager':
        setAppliedHiringManager(undefined);
        setDraftHiringManager(undefined);
        break;
      case 'Internal Recruiter':
        setAppliedInternalRecruiter(undefined);
        setDraftInternalRecruiter(undefined);
        break;
      case 'Tags': {
        const newTags = appliedTags.filter((tag) => tag !== value);
        setAppliedTags(newTags);
        setDraftTags(newTags);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    getEmployeeList('', 1, 10)
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          response.json().then((errorData) => {
            toast.error(errorData.error, { position: 'bottom-center' });
          });
        }
      })
      .then((data) => {
        setDefaultEmployeeUserList(data.employee);
      })
      .catch(() => {
        toast.error('Error fetching employees', { position: 'bottom-center' });
      });
  }, []);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      fetchJobApplicants(pageNumber);
    }, 500); // Shorter debounce for cleaner feeling

    return () => clearTimeout(debounceSearchTerm);
  }, [
    appliedInternalRecruiter,
    appliedHiringManager,
    appliedJobReferenceNumber,
    appliedJobTitle,
    appliedTags,
    appliedDateRange,
    sort,
    activeTab,
  ]);

  const defaultEmployeeUserOptions = defaultEmployeeUserList.map(
    (employee) => ({
      value: employee.id,
      label: `${employee.user.firstName} ${employee.user.lastName}`,
    }),
  );
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const fetchJobApplicants = (page: number) => {
    setLoading(true);
    setIsFetching(true);
    const requestBody: JobOpeningRequest = {
      jobTitle: appliedJobTitle,
      jobReferenceNumber: appliedJobReferenceNumber,
      hiringManager: appliedHiringManager ? appliedHiringManager.value : '',
      internalRecruiter: appliedInternalRecruiter
        ? appliedInternalRecruiter.value
        : '',
      tags: appliedTags.length > 0 ? appliedTags : undefined,
      sort,
      active: activeTab === 'Active Job Openings' ? true : false,
    };

    getJobOpeningList(requestBody, page, 10)
      .then((response) => {
        if (!response.ok) {
          throw new Error();
        }
        return response.json();
      })
      .then((data) => {
        setJobOpeningData(data.jobOpenings);
        setTotalRecords(data.totalJobOpenings);
        setTotalPages(Math.ceil(data.totalJobOpenings / 10));
        setHasFetchedOnce(true); // Mark that we have fetched at least once
      })
      .catch(() => {
        toast.error('Error Getting Job Applicants', {
          position: 'bottom-center',
        });
        setTotalPages(1);
        setJobOpeningData([]);
        setHasFetchedOnce(true);
      })
      .finally(() => {
        setLoading(false);
        setIsFetching(false);
      });
  };

  const handleManagerChange = (
    selectedOption: SelectOptions,
    isHiringManager: boolean,
  ) => {
    if (isHiringManager) {
      setDraftHiringManager(selectedOption);
    } else {
      setDraftInternalRecruiter(selectedOption);
    }
  };

  const handleStatus = (selectedOption: SelectOptions) => {
    console.log(selectedOption);
  };

  const onPageChange = (page: number) => {
    setPageNumber(page);
    fetchJobApplicants(page);
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSort((prevSort) => {
      const newSort: sortJobOpening = { ...prevSort };
      if (newSort[key as keyof sortJobOpening] === direction) {
        delete newSort[key as keyof sortJobOpening];
      } else {
        newSort[key as keyof sortJobOpening] = direction;
      }
      return newSort;
    });
  };

  return {
    loading,
    setLoading,
    handleManagerChange: (opt: SelectOptions, isHiring: boolean) => {
      if (isHiring) setDraftHiringManager(opt);
      else setDraftInternalRecruiter(opt);
    },
    loadOptionsDebounced,
    defaultEmployeeUserOptions,
    jobTitle: draftJobTitle,
    setJobTitle: setDraftJobTitle,
    setAppliedJobTitle: (val: string) => {
      setAppliedJobTitle(val);
      setDraftJobTitle(val); // Keep draft in sync for when they open the drawer
      setPageNumber(1);
    },
    tags: draftTags,
    setTags: setDraftTags,
    hiringManager: draftHiringManager,
    internalRecruiter: draftInternalRecruiter,
    jobReferenceNumber: draftJobReferenceNumber,
    setJobReferenceNumber: setDraftJobReferenceNumber,
    handleSortChange,
    jobOpeningData,
    onPageChange,
    pageNumber,
    isFetching,
    totalPages,
    history,
    sort,
    appliedFilters,
    handleClearFilters,
    handleApplyFilters,
    handleRemoveFilter,
    handleStatus,
    isFiltersApplied,
    hasFetchedOnce,
    totalRecords,
    setActiveTab,
    activeTab,
    tabs,
    filterOpen,
    handleOpenFilter,
    handleCloseFilter,
    dateRange: draftDateRange,
    setDateRange: setDraftDateRange,
  };
};
